import { Button, InlineLoading, Modal } from '@wfp/react';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { fileNameMap } from 'src/constants';
import type { AcrFileType } from 'src/crf';
import { resolveCrfByPeriod } from 'src/crf';
import { receiveAPIResponseHoldingData } from 'src/redux/api/actions';
import { apiCallAsync } from 'src/redux/api/saga';
import { REPORT_LIST_DATA_KEY } from 'src/redux/constants';
import { showNotification } from 'src/utils';
import ACRUploadErrorMessage from '../ACRUploadErrorMessage';
import ACRUploadNotificationError from '../ACRUploadNotificationError';

interface Props {
  pk: string | number;
  type: string;
  projectCode?: string;
  period?: string | number;
}

const ACRFileUploadButton = ({ pk, type, projectCode, period }: Props) => {
  const [modal, setModal] = useState(false);
  const [fileSelected, setFileSelected] = useState<File | null>(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [fileSelectedError, setFileSelectedError] = useState('');

  const dispatch = useDispatch();

  const crf = resolveCrfByPeriod(period);

  useEffect(() => {
    if (!modal) {
      setFileSelected(null);
      setFileSelectedError('');
      setFileUploading(false);
    }
  }, [modal]);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newFile = e.target.files?.[0];

    if (!newFile) return;

    const error = crf.validateFile(newFile, {
      projectsCodesList: projectCode ? [projectCode] : [],
      doneProjectsCodesList: [],
      expectedType: type as AcrFileType,
    });

    if (error) {
      setFileSelectedError(error);
      return;
    }

    setFileSelectedError('');
    setFileSelected(newFile);
  }

  async function handleFileUpload() {
    setFileUploading(true);

    const formData = new FormData();

    formData.append('pk', String(pk));
    formData.append('type', type);
    formData.append('period', String(period));
    if (fileSelected) {
      formData.append('file', fileSelected, fileSelected.name);
    }

    const url = crf.uploadUrl();

    try {
      const publicationList = await apiCallAsync(url, formData, 'post');

      dispatch(
        receiveAPIResponseHoldingData(
          publicationList.data,
          REPORT_LIST_DATA_KEY,
        ),
      );

      setFileUploading(false);
      showNotification('File uploaded successfully', 'success');
      setModal(false);
    } catch (err) {
      console.log(err);

      setFileUploading(false);
      showNotification(
        <ACRUploadNotificationError errors={err?.response?.data} />,
        'error',
      );
      setModal(false);
    }
  }

  return (
    <>
      <Button onClick={() => setModal(true)} data-test-id="choose-file-button">
        Choose file
      </Button>
      {modal && (
        <Modal
          className="acr-file-upload-modal"
          data-test-id="file-upload-popup"
          open={true}
          hideClose
          modalLabel={`File upload - ${projectCode} - ${fileNameMap?.[type]}`}
          onRequestClose={() => null}
          secondaryButtonText="Cancel"
          onSecondarySubmit={() => setModal(false)}
          secondaryButtonDisabled={false}
          primaryButtonText="Upload"
          onRequestSubmit={handleFileUpload}
          primaryButtonDisabled={!fileSelected}
        >
          <input
            type="file"
            hidden
            onChange={handleFileChange}
            accept="application/pdf"
            id="acr-single-upload"
          />
          <label
            htmlFor="acr-single-upload"
            className="acr-upload-label"
            data-test-id="choose-file-button"
          >
            Choose file
          </label>
          {fileUploading && (
            <InlineLoading
              className="acr-file-upload-loader"
              description="Uploading..."
            />
          )}
          {fileSelectedError && (
            <ACRUploadErrorMessage error={fileSelectedError} />
          )}
          {fileSelected && !fileSelectedError && (
            <div className="acr-file-selected">
              <p data-test-id="selected-file-label">File selected:</p>
              <div data-test-id="selected-file-name">{fileSelected?.name}</div>
            </div>
          )}
        </Modal>
      )}
    </>
  );
};

export default ACRFileUploadButton;
